// Turns OneDrive folder and file names from the academy's Batches folder into app data.
// Pure functions (no network), shared by the admin sync screen and the tests.
//
//   Batches/
//     MA December 2025/                 -> batch: subject MA, starts Dec 2025
//       8 Dec 25 Syllabus introduction/ -> class on 2025-12-08, title "Syllabus introduction"
//         recording.mp4, notes.pdf      -> class files
//     MA Revision Kit 2026/             -> revision material for MA

export const SUBJECTS = ['FA', 'MA', 'BT', 'LW', 'TX-UK', 'PM', 'AA', 'FR', 'FM', 'SBL', 'SBR', 'AFM', 'APM', 'ATX', 'AAA'];

const MONTHS = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4, may: 5, jun: 6, june: 6,
  jul: 7, july: 7, aug: 8, august: 8, sep: 9, sept: 9, september: 9, oct: 10, october: 10,
  nov: 11, november: 11, dec: 12, december: 12,
};

const VIDEO_EXT = /\.(mp4|m4v|mov|mkv|webm|avi|wmv|mpg|mpeg)$/i;

const tidy = (s) => String(s || '').replace(/\s+/g, ' ').trim();
const pad = (n) => String(n).padStart(2, '0');

function fullYear(y) {
  const n = Number(y);
  return y.length === 2 ? 2000 + n : n;
}

// Returns 'YYYY-MM-DD' only for real calendar dates.
export function isoDate(year, month, day) {
  if (!year || !month || !day) return null;
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return null;
  if (year < 2015 || year > 2100) return null;
  return `${year}-${pad(month)}-${pad(day)}`;
}

export function subjectFrom(name) {
  const first = tidy(name).toUpperCase().split(/[\s_]+/)[0] || '';
  if (/^TX(-?UK)?$/.test(first) || /^TX\b/.test(tidy(name).toUpperCase())) return 'TX-UK';
  const code = first.replace(/[^A-Z-]/g, '');
  return SUBJECTS.includes(code) ? code : null;
}

// "MA December 2025" -> { subject: 'MA', kind: 'batch', start_month: '2025-12-01' }
export function parseBatchName(name) {
  const text = tidy(name);
  const subject = subjectFrom(text);
  const m = text.match(/\b([A-Za-z]{3,9})\.?\s+(\d{4})\b/);
  let start_month = null;
  if (m && MONTHS[m[1].toLowerCase()]) start_month = isoDate(Number(m[2]), MONTHS[m[1].toLowerCase()], 1);
  const kind = /revision/i.test(text) ? 'revision' : start_month ? 'batch' : 'other';
  return { subject, kind, start_month };
}

// "9 Dec 25 Accounting for management Part-1" -> { class_date: '2025-12-09', title: 'Accounting for management Part-1' }
export function parseClassName(name) {
  const text = tidy(name);
  let m = text.match(/^(\d{1,2})(?:st|nd|rd|th)?[\s.\-/]+([A-Za-z]{3,9})\.?[\s.\-/,]+(\d{4}|\d{2})\b[\s:\-–—]*(.*)$/);
  if (m && MONTHS[m[2].toLowerCase()]) {
    const date = isoDate(fullYear(m[3]), MONTHS[m[2].toLowerCase()], Number(m[1]));
    if (date) return { class_date: date, title: tidy(m[4]) || text };
  }
  m = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})\b[\s:\-–—]*(.*)$/);
  if (m) {
    const date = isoDate(Number(m[1]), Number(m[2]), Number(m[3]));
    if (date) return { class_date: date, title: tidy(m[4]) || text };
  }
  m = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4}|\d{2})\b[\s:\-–—]*(.*)$/); // Indian order: day/month/year
  if (m) {
    const date = isoDate(fullYear(m[3]), Number(m[2]), Number(m[1]));
    if (date) return { class_date: date, title: tidy(m[4]) || text };
  }
  return { class_date: null, title: text };
}

export function fileKind(name, mime) {
  if ((mime && mime.startsWith('video/')) || VIDEO_EXT.test(name || '')) return 'video';
  if (mime === 'application/pdf' || /\.pdf$/i.test(name || '')) return 'pdf';
  return 'other';
}

// The player address Microsoft gives under ... > Embed for a video in OneDrive or SharePoint.
export function embedUrl(sharepointIds) {
  if (!sharepointIds || !sharepointIds.siteUrl || !sharepointIds.listItemUniqueId) return null;
  const site = sharepointIds.siteUrl.replace(/\/+$/, '');
  const embed = encodeURIComponent(JSON.stringify({ ust: true, hv: 'CopyEmbedCode' }));
  return `${site}/_layouts/15/embed.aspx?UniqueId=${encodeURIComponent(sharepointIds.listItemUniqueId)}`
    + `&embed=${embed}&referrer=StreamWebApp&referrerScenario=EmbedDialog.Create`;
}

// Graph "shares" id for a sharing link: u! + base64url(link)
export function shareId(url) {
  const bytes = new TextEncoder().encode(String(url).trim());
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return 'u!' + btoa(bin).replace(/=+$/, '').replace(/\//g, '_').replace(/\+/g, '-');
}

export function toFile(item) {
  const kind = fileKind(item.name, item.file && item.file.mimeType);
  return {
    item_id: item.id,
    name: item.name,
    kind,
    mime: (item.file && item.file.mimeType) || null,
    size: typeof item.size === 'number' ? item.size : null,
    duration_ms: item.video && typeof item.video.duration === 'number' ? item.video.duration : null,
    web_url: item.webUrl || null,
    embed_url: kind === 'video' ? embedUrl(item.sharepointIds) : null,
  };
}

// Build the payload for admin_sync_batch from a batch folder and what's inside it.
//   batchItem:      the batch folder (driveItem)
//   children:       driveItems directly inside the batch folder
//   classChildren:  Map(class folder id -> driveItems inside that class folder)
export function buildBatchPayload(batchItem, driveId, children, classChildren) {
  const parsed = parseBatchName(batchItem.name);
  const classes = [];
  const loose = [];
  for (const child of children) {
    if (child.folder) {
      const { class_date, title } = parseClassName(child.name);
      const files = (classChildren.get(child.id) || []).filter((f) => f.file).map(toFile);
      classes.push({ item_id: child.id, folder_name: child.name, title, class_date, web_url: child.webUrl || null, files });
    } else if (child.file) {
      loose.push(toFile(child));
    }
  }
  if (loose.length) {
    classes.push({
      item_id: `loose:${batchItem.id}`,
      folder_name: batchItem.name,
      title: parsed.kind === 'revision' ? 'Revision material' : 'Batch material',
      class_date: null,
      web_url: batchItem.webUrl || null,
      is_loose: true,
      files: loose,
    });
  }
  return {
    item_id: batchItem.id,
    drive_id: driveId,
    name: tidy(batchItem.name),
    web_url: batchItem.webUrl || null,
    subject: parsed.subject,
    kind: parsed.kind,
    start_month: parsed.start_month,
    classes,
  };
}
